import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { Icon } from '@noths/polaris-design-system-core-components';

import { formatRating, ratingLabel } from '../../../utils/formatRating';
import * as styles from './ProductCardRating.styles';

interface ProductCardRatingProps {
  isNew: boolean;
  rating: number | undefined;
}

/**
 * The right-hand end of the price row: an average rating, or "NEW" for a product
 * that has none yet.
 *
 * A rating wins over `isNew`, since a product with reviews is no longer new in
 * the sense the flag is making. Renders nothing when there is neither, so the
 * price simply keeps the whole row.
 */
export const ProductCardRating = ({ isNew, rating }: ProductCardRatingProps) => {
  if (rating !== undefined && Number.isFinite(rating)) {
    const formatted = formatRating(rating);

    return (
      <span css={styles.rating} data-testid="product-card-rating">
        <span css={styles.star}>
          <Icon aria-hidden data-icon="starFull" name="starFull" size="sm" />
        </span>
        {/* The whole sentence is one hidden node, and the visible number is
            hidden from assistive technology, so it announces as "Rated 4.8 out
            of 5" rather than as three nodes run together. */}
        <span css={visuallyHidden}>{ratingLabel(rating)}</span>
        <span aria-hidden>{formatted}</span>
      </span>
    );
  }

  if (isNew) {
    return (
      <span css={[styles.rating, styles.isNew]} data-testid="product-card-new">
        <span css={styles.star}>
          <Icon aria-hidden data-icon="starEmpty" name="starEmpty" size="sm" />
        </span>
        NEW
      </span>
    );
  }

  return null;
};

ProductCardRating.displayName = 'ProductCardRating';
